import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Dayjs } from "utils/dayjs"

import OperationHoursRecord from "models/OperationHoursRecord"
import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import { Grid, GridItem } from "components/Grid"
import Status, { StatusType } from "components/Status/Status"

interface ISaveRecordConfirmModalProps {
    operationHours: number
    operationHoursTime: Dayjs
    recordedOperationHours?: OperationHoursRecord[]
    onClose(): void
    onConfirm(): void
}

const SaveRecordConfirmModal: FC<ISaveRecordConfirmModalProps> = ({
    operationHours,
    operationHoursTime,
    recordedOperationHours,
    onClose,
    onConfirm,
}) => {
    const { t } = useTranslation()

    function recordWithSameTimeExists() {
        return recordedOperationHours?.some(r => r.recordedOn.isSame(operationHoursTime)) || false
    }

    function isEarlierThanEarliestRecordAndHasMoreHours() {
        if (!recordedOperationHours?.length) return false
        const earliestRecord = recordedOperationHours[recordedOperationHours.length - 1]
        return operationHoursTime.isBefore(earliestRecord.recordedOn) && operationHours > earliestRecord.operationHours
    }

    function isLaterThanLatestRecordAndHasLessHours() {
        if (!recordedOperationHours?.length) return false
        const latestRecord = recordedOperationHours[0]
        return operationHoursTime.isAfter(latestRecord.recordedOn) && operationHours < latestRecord.operationHours
    }

    function isNotSubsequentHours() {
        if (!recordedOperationHours?.length) return false

        const recordsAfter = recordedOperationHours.filter(r => r.recordedOn.isAfter(operationHoursTime))
        if (recordsAfter.length && recordsAfter.some(r => r.operationHours < operationHours)) {
            return true
        }

        const recordsBefore = recordedOperationHours.filter(r => r.recordedOn.isBefore(operationHoursTime))
        if (recordsBefore.length && recordsBefore.some(r => r.operationHours > operationHours)) {
            return true
        }

        return false
    }

    const shouldShowWarning: boolean = recordWithSameTimeExists()
        || isEarlierThanEarliestRecordAndHasMoreHours()
        || isLaterThanLatestRecordAndHasLessHours()
        || isNotSubsequentHours()

    return (
        <ConfirmModal
            title={t("asset.saveRecord")}
            maxWidth="586px"
            onClose={onClose}
            onConfirm={onConfirm}
            confirmButtonLabel={t("common.save")}>
            {t("asset.saveRecordIntro")}
            <Grid columns="minmax(0, max-content) minmax(0, max-content)"
                gap="3.2rem"
                className="my-4">
                <GridItem>
                    {t("asset.operatingHours")}
                    <div className="font-bold mt-half">
                        {t("common.xHours", { count: operationHours })}
                    </div>
                </GridItem>
                <GridItem>
                    {t("asset.recordedOn")}
                    <div className="font-bold mt-half">
                        {operationHoursTime.format(t("config.dateLongTimeFormat"))}
                    </div>
                </GridItem>
            </Grid>
            {shouldShowWarning &&
                <Status type={StatusType.caution}>
                    {t("asset.saveRecordWarning")}
                </Status>
            }
            <div className="mt-4">
                {t("asset.saveRecordOutro")}
            </div>
        </ConfirmModal>
    )
}

export default SaveRecordConfirmModal
